import { paymentService, type PaymentPackage } from '../../services'
import {
  clearPendingPlan,
  decideAutoCheckout,
  findPackageForMarketingPlan,
  hasBlockingSubscription,
  readPendingPlan,
  type MarketingPlan,
} from './marketing-plan'

export type CheckoutRedirectResult =
  | 'redirected'
  | 'no-plan'
  | 'unavailable'
  | 'failed'
  | 'preselect'

type SubscriptionSnapshot = {
  stripeSubscriptionId?: string | null
  status?: string | null
  packageId?: string | null
} | null

type CheckoutDeps = {
  listPackages: () => Promise<PaymentPackage[]>
  getSubscription: () => Promise<SubscriptionSnapshot>
  createCheckoutSession: (payload: { packageId: string }) => Promise<{ url?: string }>
  assignLocation: (url: string) => void
}

export const pendingCheckoutNavigation = {
  assign(url: string) {
    window.location.assign(url)
  },
}

const defaultDeps: CheckoutDeps = {
  listPackages: () => paymentService.listPackages(),
  getSubscription: () => paymentService.getSubscription(),
  createCheckoutSession: payload => paymentService.createCheckoutSession(payload),
  assignLocation: url => pendingCheckoutNavigation.assign(url),
}

let cached: { plan: MarketingPlan; promise: Promise<CheckoutRedirectResult> } | null = null

export function resetPendingCheckoutForTests() {
  cached = null
}

function asId(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null
}

async function execute(plan: MarketingPlan, deps: CheckoutDeps): Promise<CheckoutRedirectResult> {
  try {
    const [packages, subscription] = await Promise.all([
      deps.listPackages(),
      deps.getSubscription().catch(() => null),
    ])
    const match = findPackageForMarketingPlan(packages, plan)
    if (!match) return 'unavailable'

    const ranked = [...packages]
      .filter(pkg => pkg.isActive !== false)
      .sort((a, b) => Number(a.amount) - Number(b.amount))
    const tierOf = (id: string | null) => {
      if (!id) return null
      const index = ranked.findIndex(pkg => pkg.id === id)
      return index >= 0 ? index + 1 : null
    }
    const activePackageId = asId(subscription?.packageId)
    const decision = decideAutoCheckout({
      plan,
      packageId: match.id,
      hasStripeSubscription: hasBlockingSubscription(
        subscription?.status,
        asId(subscription?.stripeSubscriptionId)
      ),
      activePackageId,
      activeTier: tierOf(activePackageId),
      targetTier: tierOf(match.id),
    })

    if (decision.action !== 'checkout') return 'preselect'

    const session = await deps.createCheckoutSession({ packageId: match.id })
    if (!session?.url) return 'failed'
    clearPendingPlan()
    deps.assignLocation(session.url)
    return 'redirected'
  } catch {
    return 'failed'
  }
}

/**
 * Start Stripe Checkout for the package stored from `?plan=`.
 * Repeated calls for the same plan share one request so React Strict Mode
 * does not open two Checkout sessions.
 */
export function redirectToCheckoutForPendingPlan(
  deps: CheckoutDeps = defaultDeps
): Promise<CheckoutRedirectResult> {
  const plan = readPendingPlan()
  if (!plan) return Promise.resolve('no-plan')
  if (cached?.plan === plan) return cached.promise
  const promise = execute(plan, deps)
  cached = { plan, promise }
  return promise
}
